import { useQuery } from '@tanstack/react-query'
import { Constants } from 'constants/constants'
import { QueryKeys } from 'constants/query-keys'
import {
    fetchSafetyStoreLagging,
    fetchSafetyStoreTotal,
    fetchSafetyStoreLeading,
} from 'queries/safety/safety'
import { SafetyFetchProps } from 'types/api'
import { IResponse } from 'utils/api'
import { TIME } from 'utils/misc'
import { useAnalytics } from 'utils/analytics'
import {
    getGreenfieldDrillValues,
    getGreenfieldLocationFilter,
} from 'utils/greenfield'

const getSafetyFilters = (location: string) => ({
    ...getGreenfieldLocationFilter(location),
    ...getGreenfieldDrillValues(location),
})

export function useSafetyStoreLagging({
    env,
    accessToken,
    location,
}: Omit<SafetyFetchProps, 'dynamicSubPath'>): IResponse {
    const { trackEvent } = useAnalytics()
    const { tenMinutes } = TIME
    const greenfieldCardNumber = Constants.GREENFIELD_CARDS.SAFETY.STORE_LAGGING
    const safetyFilters = getSafetyFilters(location)

    const { data, isLoading, isError, refetch, remove, error } = useQuery(
        [QueryKeys.SAFETY_STORE_LAGGING, location],
        () =>
            fetchSafetyStoreLagging({
                env,
                accessToken,
                dynamicSubPath: greenfieldCardNumber,
                filters: safetyFilters,
                trackEvent,
            }),
        {
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
        },
    )

    return { data, isLoading, isError, refetch, remove, error }
}

export function useSafetyStoreTotal({
    env,
    accessToken,
    location,
}: Omit<SafetyFetchProps, 'dynamicSubPath'>): IResponse {
    const { trackEvent } = useAnalytics()
    const { tenMinutes } = TIME
    const greenfieldCardNumber = Constants.GREENFIELD_CARDS.SAFETY.STORE_TOTAL
    const safetyFilters = getSafetyFilters(location)

    const { data, isLoading, isError, refetch, remove, error } = useQuery(
        [QueryKeys.SAFETY_STORE_TOTAL, location],
        () =>
            fetchSafetyStoreTotal({
                env,
                accessToken,
                dynamicSubPath: greenfieldCardNumber,
                filters: safetyFilters,
                trackEvent,
            }),
        {
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
        },
    )

    return { data, isLoading, isError, refetch, remove, error }
}

export function useSafetyStoreLeading({
    env,
    accessToken,
    location,
}: Omit<SafetyFetchProps, 'dynamicSubPath'>): IResponse {
    const { trackEvent } = useAnalytics()
    const { tenMinutes } = TIME
    const greenfieldCardNumber = Constants.GREENFIELD_CARDS.SAFETY.STORE_LEADING
    const safetyFilters = getSafetyFilters(location)

    const { data, isLoading, isError, refetch, remove, error } = useQuery(
        [QueryKeys.SAFETY_STORE_LEADING, location],
        () =>
            fetchSafetyStoreLeading({
                env,
                accessToken,
                dynamicSubPath: greenfieldCardNumber,
                filters: safetyFilters,
                trackEvent,
            }),
        {
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
        },
    )

    return { data, isLoading, isError, refetch, remove, error }
}
